import React, { useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Home, AlertOctagon } from 'lucide-react';

/**
 * Catch-all for any unknown route.
 *
 * Two modes:
 *  1. `blocked=true` — the URL matches a well-known vulnerability-probe pattern
 *     (WordPress, PHP shells, env / git files). We render a minimal "Gone" page
 *     with document.title = "Blocked" + noindex so analytics tools can filter
 *     these bot pageviews out of legitimate site traffic. We can't return HTTP
 *     410 from a SPA (the static host serves 200 for every path) but we can at
 *     least stop counting them as real "top pages" in analytics dashboards.
 *  2. Everything else — friendly "Page not found" with a back-home link.
 *
 * Either way we inject `<meta name="robots" content="noindex, nofollow">` so
 * search engines don't accidentally index typos or old links as real pages.
 */
const PROBE_PATTERNS = [
  /\.php(\?|$|\/)/i,       // any .php path
  /^\/wp-/i,               // WordPress
  /^\/wordpress/i,
  /^\/xmlrpc/i,
  /^\/\.env/i,             // .env exfil attempts
  /^\/\.git/i,             // .git exposure
  /^\/\.aws/i,
  /^\/\.ssh/i,
  /^\/\.htaccess/i,
  /^\/phpmyadmin/i,
  /^\/phpinfo/i,
  /^\/administrator/i,
  /^\/admin\.php/i,
  /^\/backup/i,
  /^\/vendor\//i,
  /^\/config\.(json|yml|yaml)$/i,
];

const isProbe = (path) => PROBE_PATTERNS.some((rx) => rx.test(path));

export default function NotFoundPage() {
  const { pathname, search } = useLocation();
  const blocked = useMemo(() => isProbe(pathname + search), [pathname, search]);

  useEffect(() => {
    document.title = blocked ? 'Blocked — Recruitizr' : 'Page not found — Recruitizr';

    // Inject/replace the robots meta so unknown paths are never indexed.
    let meta = document.querySelector('meta[name="robots"]');
    const prev = meta?.getAttribute('content') || null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      // Restore previous robots value on unmount so real pages regain indexing.
      if (prev) meta.setAttribute('content', prev);
    };
  }, [blocked]);

  if (blocked) {
    // Intentionally sparse so this doesn't look like a real page in analytics.
    return (
      <div
        data-testid="probe-blocked-page"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0f',
          color: '#94a3b8',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          fontSize: 14,
          textAlign: 'center',
          padding: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 48, opacity: 0.4 }}>410</div>
          <div>Gone</div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-200 px-6"
    >
      <AlertOctagon className="h-14 w-14 text-slate-500 mb-6" />
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Page not found</h1>
      <p className="text-slate-400 text-base sm:text-lg max-w-md text-center mb-8">
        The page you&apos;re looking for doesn&apos;t exist or has moved. Let&apos;s get you back
        to something that does.
      </p>
      <div className="flex gap-3 flex-wrap justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-slate-900 hover:bg-slate-100 transition-colors font-medium"
          data-testid="not-found-home-link"
        >
          <Home className="h-4 w-4" /> Back to home
        </Link>
        <Link
          to="/help"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors font-medium"
          data-testid="not-found-help-link"
        >
          Visit Help Centre
        </Link>
      </div>
      <p className="text-xs text-slate-600 mt-10 font-mono">
        {pathname}
      </p>
    </div>
  );
}
